import * as Commons from "oc-commons/Commons";
import * as Scenes from "../Scenes";
import "../patched/TextureLoader.oc";
import "oc-commons/extensions/StringExtensions";
import "../patched/MTLLoader.oc";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { ColladaLoader } from "three/examples/jsm/loaders/ColladaLoader";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { TDSLoader } from "three/examples/jsm/loaders/TDSLoader";
import * as fflate from "fflate";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import * as Textures from "../Textures";
import { Urls } from "oc-commons/Urls";
import { MTLLoader } from "../patched/MTLLoader.oc";
import { TextureLoader } from "../patched/TextureLoader.oc";
import { MyLoadingManager } from "../patched/MyLoadingManager";
import * as THREE from "three";

import * as _ from "lodash";
import {
  AnimationClip,
  AnimationMixer,
  BufferAttribute,
  BufferGeometry,
  Cache,
  CubeTexture,
  CubeTextureLoader,
  DataTexture,
  Float32BufferAttribute,
  ImageUtils,
  InterleavedBufferAttribute,
  LoadingManager,
  Mapping,
  Material,
  Mesh,
  MeshPhongMaterial,
  Object3D,
  PMREMGenerator,
  RGBAFormat,
  Texture,
} from "three";
import { IReplacingMaterialFactory } from "./ReplacingMaterialFactory";
import { IMaterialFactory } from "./AbstractMaterialFactory";

// Global declaration
declare const STAGE: any;

interface IObject3DWithAnimations extends Object3D {
  animationClips?: AnimationClip[];
  sourceFile?: string;
}

interface IMTLCreator {
  preload(): void;
}

interface ILoaderCache {
  [cacheKey: string]: Promise<IObject3DWithAnimations>;
}

interface ITextureWithSource extends Texture {
  sourceFile?: string;
}

interface ICubeTextureWithSource extends CubeTexture {
  sourceFiles?: string[];
}

interface IObject3DWithMaterial extends Object3D {
  material?: Material | Material[];
}

interface IGeometryWithAttributes extends BufferGeometry {
  attributes: {
    uv?: BufferAttribute;
    [key: string]: BufferAttribute | InterleavedBufferAttribute | undefined;
  };
}

interface IMeshWithGeometry extends Mesh {
  geometry: IGeometryWithAttributes;
}

type SuccessCallback = (...args: any[]) => any;
type ErrorCallback = (error: Error | unknown) => void;
type ProgressCallback = (event: ProgressEvent) => void;

(self as any).fflate = fflate;

const wrappedProgressEventRejectWorkaround =
  (reject: (reason: Error) => void): ((error: Error | ProgressEvent | unknown) => void) =>
  (error: Error | ProgressEvent | unknown): void => {
    // debugger;
    // Aus irgend einem komischen Grund kommt hier ProgressEvent als Rückgabe bei einem 404 an !?!?
    if (error instanceof ProgressEvent) {
      return reject(
        new Error(
          JSON.stringify({
            status: _.get(error, "target.status"),
            statusText: _.get(error, "target.statusText"),
            url: _.get(error, "target.responseURL"),
          }),
        ),
      );
    }
    return reject(error as Error);
  };

const startEndLoadingEventPromiseEvenIfCached = <T extends IObject3DWithAnimations>(
  promise: Promise<T>,
): Promise<T> => {
  const loadingManager = UniversalLoaderClass.INSTANCE.loadingManager;
  const url = "cache";
  loadingManager.itemStart(url);
  return promise.then(
    (object: T): T => {
      loadingManager.itemEnd(url);
      return object;
    },
    (error: Error): never => {
      console.error("UniversalLoader.startEndLoadingEventPromiseEven IfCached error", error);
      loadingManager.itemError(url);
      throw error;
    },
  );
};

const objectClonerPromise = (
  promise: Promise<IObject3DWithAnimations>,
): Promise<IObject3DWithAnimations> => {
  // clone, damit nur einmal geladen aber mehrmals instanziiert werden kann
  // Material und Geometry werden dabei nur als Referenz kopiert
  return startEndLoadingEventPromiseEvenIfCached(
    promise.then((object: IObject3DWithAnimations): IObject3DWithAnimations => {
      if (object) {
        const animationClips = object.animationClips;
        object = object.clone() as IObject3DWithAnimations;
        object.animationClips = animationClips;
      }
      return object;
    }),
  );
};

const triggerLoadedDebounced = _.debounce(
  (): void => {
    STAGE.trigger("loadingManagerOnLoad");
    STAGE.dirty();
  },
  100,
  { maxWait: 3000 },
);

/**
 * UniversalLoader lädt verschiedene Formate und bietet Caching und Nachverfolgen von Ladefehlern
 * und Events für den gesamten Ladevorgang
 */
class UniversalLoaderClass {
  static INSTANCE = new UniversalLoaderClass();

  cache: ILoaderCache;
  cacheEnabled: boolean;
  crossOrigin: string;
  loadingManager: MyLoadingManager;
  materialFactory: IReplacingMaterialFactory | undefined;
  materialsWithAllImagesLoaded = new Set<Material>();

  constructor() {
    this.cache = {};
    this.cacheEnabled = true;

    // enable general caching in ThreeJS, but not for Models (this is done by our own cache) see this._callLoad
    Cache.enabled = true;

    // allow CORS
    this.crossOrigin = "anonymous";
    (ImageUtils as { crossOrigin?: string }).crossOrigin = this.crossOrigin;

    // der von UniversalLoader benutzte Loadingmanager
    this.loadingManager = new MyLoadingManager();
  }

  /**
   * lädt eine MTL-Datei. Achtung: Erst wenn successCallback aufgerufen wird,
   * darf man Modelle laden! Ansonsten wundert man sich, dass Modelle weiß sind..
   */
  loadMTL(
    mtlUrl: string,
    successCallback: SuccessCallback,
    errorCallback?: ErrorCallback,
    progressCallback?: ProgressCallback,
  ): void {
    let mtlLoader = new MTLLoader(this.loadingManager);
    let path = mtlUrl.substringBeforeLast("/");
    if (!(Commons.isEmpty(path) || path.endsWith("/"))) {
      path += "/";
    }
    mtlLoader.setResourcePath(path);
    mtlLoader.setPath(path);
    mtlLoader.load(
      mtlUrl.substringAfterLast("/"),
      (materials: IMTLCreator): void => {
        materials.preload();
        successCallback(materials);
      },
      progressCallback,
      errorCallback,
    );
  }

  /** */
  setMaterialFactory(materialsFactory: IReplacingMaterialFactory): void {
    this.materialFactory = materialsFactory;
  }

  getMaterialFactory(): IReplacingMaterialFactory | undefined {
    return this.materialFactory;
  }

  /**
   * lädt ein Modell, entweder STL, DAE, JS oder OBJ
   * @param urlsArray ["Modellname.extension"], im Falle einer OBJ-Datei können es auch zwei
   *     Namen sein ["Modell.OBJ","DieMaterialien.MTL"]
   * @param successCallback
   * @param errorCallback
   * @param progressCallback deprecated!
   * @param materialsFactory
   * @param cacheKey
   */
  loadModel(
    urlsArray: string | string[],
    successCallback?: SuccessCallback,
    errorCallback?: ErrorCallback,
    progressCallback?: ProgressCallback,
    materialsFactory?: IReplacingMaterialFactory,
    cacheKey?: string,
  ): void {
    if (progressCallback) {
      console.warn("UniversalLoader.loadModel progressCallback is deprecated");
    }
    let promise = this.loadModelPromise(urlsArray, materialsFactory, cacheKey);
    promise.then(successCallback, errorCallback);
  }

  /**
   * lädt ein Modell, entweder STL, DAE, JS oder OBJ
   * @param {(string | string[])} urlOrArray die URL der Modelldatei wie "Modellname.extension", im Falle einer
   *     OBJ-Datei können es auch zwei Namen sein ["Modell.OBJ","DieMaterialien.MTL"]
   * @param {*} [materialsFactory=undefined] optional eine AbstractMaterialFactory
   * @param {*} [cacheKey=undefined] optional, ein String. Sollte bei der Verwendung einer
   *     ReplacingMaterialsFactory gesetzt sein!
   * @returns {Promise<THREE.Object3D>} Promise mit dem geladenen THREE.Objekt3D
   */
  loadModelPromise(
    urlOrArray: string | string[],
    materialsFactory?: IMaterialFactory,
    cacheKey?: string,
  ): Promise<IObject3DWithAnimations> {
    if (Commons.isEmpty(urlOrArray)) {
      return Promise.resolve(null as unknown as IObject3DWithAnimations);
    }

    if (materialsFactory) {
      const orig = materialsFactory;
      materialsFactory = {
        create: (name: string): Material => {
          const mat = orig.create(name);
          return mat;
        },
      };
    }

    this.startSomeLoading();

    const urls = typeof urlOrArray === "string" ? [urlOrArray] : urlOrArray;

    const cacheKeyIntern = cacheKey == null ? urls.join("|") : cacheKey;

    let promise: Promise<IObject3DWithAnimations> | undefined;

    if (this.cacheEnabled) {
      promise = this.cache[cacheKeyIntern];
      // console.log('cache access',cacheKeyIntern,promise);
      if (promise) {
        return objectClonerPromise(promise);
      }
    }

    promise = this._loadModelPromise(urls, materialsFactory);

    if (this.cacheEnabled) {
      this.cache[cacheKeyIntern] = promise;
      // console.log('cache write',cacheKeyIntern,this.cache[cacheKeyIntern]);

      promise = objectClonerPromise(promise);
    }

    return promise;
  }

  /** */
  getPromiseAllImagesLoaded(promise: Promise<Object3D>): Promise<Object3D> {
    return promise.then((object3D: Object3D): Promise<Object3D> => {
      const materials = Textures.getMaterials(object3D);
      const promises: Promise<Texture>[] = [];
      const texturesSet = new Set<Texture>();
      materials
        .filter((mat: Material): boolean => !this.materialsWithAllImagesLoaded.has(mat))
        .forEach((mat: Material): void => {
          for (let textureKey in mat) {
            if (textureKey.endsWith("Map") || textureKey === "map") {
              const texture = mat[textureKey];
              if (
                texture != null &&
                texture instanceof Texture &&
                !texturesSet.has(texture) &&
                texture.image == null
              ) {
                // console.log({textureKey, texture})
                const promise = UniversalLoaderClass.INSTANCE.getTexturePromise(texture);
                promises.push(promise);
                texturesSet.add(texture);
              }
            }
          }
        });
      if (promises.length == 0) {
        materials.forEach((material: Material) => this.materialsWithAllImagesLoaded.add(material));
        return Promise.resolve(object3D);
      }
      const textures = Array.from(texturesSet);
      let reacted = false;
      Commons.sleep(3000).then((): void => {
        if (!reacted) {
          console.error("getPromiseAllImagesLoaded timeout (3 seconds)", {
            object3D,
            materials,
            textures,
            images: textures.map(
              (t: Texture) => (t as { image?: { complete?: boolean } }).image?.complete,
            ),
          });
        }
      });

      return Promise.all(promises)
        .then((): Object3D => {
          reacted = true;
          materials.forEach((material: Material) =>
            this.materialsWithAllImagesLoaded.add(material),
          );
          // console.log('loaded', {materials, textures});
          return object3D;
        })
        .catch((error: Error): Object3D => {
          reacted = true;
          console.error("textures not loaded", { materials, textures });
          return object3D;
        });
    });
  }

  getTexturePromise(texture: Texture): Promise<Texture> {
    if (!texture?.isTexture || texture.image) {
      return Promise.resolve(texture);
    }
    const textureWithPromise = texture as any;
    textureWithPromise.promise =
      textureWithPromise.promise ??
      new Promise<Texture>(
        (resolve: (value: Texture) => void, reject: (reason: Error) => void): void => {
          textureWithPromise.promiseResolve = resolve;
          textureWithPromise.promiseReject = reject;
        },
      );
    return textureWithPromise.promise;
  }

  canLoadModelFileExtension(url: string): boolean {
    return this._getLoadMethod(url) != null;
  }

  /**
   *
   * @param url
   */
  _getLoadMethod(url: string): Function | undefined {
    const extension = Urls.urlSplit(url).path.substringAfterLast(".").toUpperCase();
    const loadMethodName = "_load" + extension;
    return this[loadMethodName];
  }

  /**
   * @returns {Promise}
   */
  _loadModelPromise(
    urls: string[],
    materialsFactory?: IMaterialFactory,
  ): Promise<IObject3DWithAnimations> {
    // Finde den passenden Loader per Reflection (this._load<SUFFIX>)
    let url: string = "";
    let method: Function | undefined;
    findLoader: {
      for (url of urls) {
        method = this._getLoadMethod(url);
        if (method) {
          break findLoader;
        }
      }
      console.warn("UniversalLoader.loadModel unknown file format " + urls.join(", "));
      return Promise.reject("unknown file format " + urls.join(", "));
    }

    // Führe den eigentlichen Loader aus
    let promise = new Promise<IObject3DWithAnimations>(
      (
        resolve: (value: IObject3DWithAnimations) => void,
        reject: (reason: Error) => void,
      ): void => {
        // console.log('start loading',url);
        method!.call(
          this,
          url,
          urls,
          resolve,
          undefined,
          wrappedProgressEventRejectWorkaround(reject),
          materialsFactory,
        );
      },
    );

    promise = promise.then((object: IObject3DWithAnimations): IObject3DWithAnimations => {
      // console.log('loaded',url);

      if (object) {
        // Füge dem Objekt den Dateinamen hinzu
        object.name = url.substringAfterLast("/");

        // if an OBJ is loaded, add a second UV map for AOmap usage
        if (/obj$/i.test(url)) {
          object.children.forEach((child: Object3D): void => {
            const mesh = child as IMeshWithGeometry;
            if (mesh.geometry?.attributes?.uv)
              mesh.geometry.setAttribute(
                "uv2",
                new Float32BufferAttribute(mesh.geometry.attributes.uv.array, 2),
              );
          });
        }
        // prevent and find out the following warning:
        // THREE js imported OBJ model [.CommandBufferContext]RENDER WARNING: Render count or primcount is 0
        Scenes.removeZeroFaceOrVerticesChildren(object);
      }
      return object;
    });

    return promise;
  }

  /**
   */
  _loadOBJ(
    url: string,
    urls: string[],
    successCallback: SuccessCallback,
    progressCallback: ProgressCallback | undefined,
    errorCallback: ErrorCallback,
    materialsFactory?: IReplacingMaterialFactory,
  ): void {
    const mtlUrl = urls.find((url: string): boolean => /^.*\.mtl$/i.test(url));
    const loadObj = (materials: IMTLCreator | IReplacingMaterialFactory | null): void => {
      const objLoader = new OBJLoader(this.loadingManager);
      const mat = materials || materialsFactory || this.materialFactory;
      if (mat) {
        objLoader.setMaterials(mat as any);
      }
      this._callLoad(objLoader, url, successCallback, progressCallback, errorCallback);
    };
    if (mtlUrl) {
      this.loadMTL(
        mtlUrl,
        loadObj,
        (error: Error | unknown): void => {
          // die MTL wird als optional betrachtet. 404 wird durch den FileLoader als Warning geloggt.
          loadObj(null);
        },
        progressCallback,
      );
    } else {
      loadObj(null);
    }
  }

  /**
   */
  _loadGLTF(
    url: string,
    urls: string[],
    successCallback: SuccessCallback,
    progressCallback: ProgressCallback | undefined,
    errorCallback: ErrorCallback,
    materialsFactory?: IReplacingMaterialFactory,
  ): void {
    const loader = new GLTFLoader(this.loadingManager);
    this._callLoad(
      loader,
      url,
      (gltf: { scene: IObject3DWithAnimations; animations: AnimationClip[] }): void => {
        gltf.scene.sourceFile = url;
        this._setMaterialsFromFactoryToObject(gltf.scene, materialsFactory);
        if (!Commons.isEmpty(gltf.animations)) {
          gltf.scene.animationClips = gltf.animations;
        }
        successCallback(gltf.scene);
      },
      progressCallback,
      errorCallback,
    );
  }

  /**
   */
  _loadGLB(
    url: string,
    urls: string[],
    successCallback: SuccessCallback,
    progressCallback: ProgressCallback | undefined,
    errorCallback: ErrorCallback,
    materialsFactory?: IReplacingMaterialFactory,
  ): void {
    return this._loadGLTF(
      url,
      urls,
      successCallback,
      progressCallback,
      errorCallback,
      materialsFactory,
    );
  }

  /**
   * @param object Object3d
   * @param materialsFactory
   */
  _setMaterialsFromFactoryToObject(
    object: Object3D,
    materialsFactory?: IReplacingMaterialFactory,
  ): void {
    if (materialsFactory == null) {
      return;
    }

    const swap = (m: Material): Material => {
      const newM = materialsFactory.create(m.name);
      return newM &&
        !(newM.userData as { _no_material_with_this_name_found?: string })
          ._no_material_with_this_name_found
        ? newM
        : m;
    };

    Scenes.filter(object, (o: IObject3DWithMaterial): boolean => o.material != null).forEach(
      (o: IObject3DWithMaterial): void => {
        if (Array.isArray(o.material)) {
          o.material = o.material.map(swap);
        } else if (o.material) {
          o.material = swap(o.material);
        }
      },
    );
  }

  loadTexturePromise(
    url: string,
    mapping?: Mapping,
    theCrossOrigin?: string,
  ): Promise<ITextureWithSource> {
    return new Promise<ITextureWithSource>(
      (resolve: (value: ITextureWithSource) => void, reject: (reason: unknown) => void): void => {
        this.loadTexture(url, mapping, resolve, reject, theCrossOrigin);
      },
    );
  }

  /**
   * von THREE.ImageUtils.loadTexture kopiert mit dem Unterschied, dass der loadingManager
   * vom UniversalLoader benutzt wird
   */
  loadTexture(
    url: string,
    mapping?: Mapping,
    onLoad?: SuccessCallback,
    onError?: ErrorCallback,
    theCrossOrigin: string = this.crossOrigin,
  ): ITextureWithSource {
    if (url.endsWith("hdr")) {
      return this.loadHdrTexture(url, mapping, onLoad, onError, theCrossOrigin);
    }

    const loader = new TextureLoader(this.loadingManager);
    loader.setCrossOrigin(theCrossOrigin);

    const finalOnError: ErrorCallback =
      onError ||
      ((): void => console.warn("UniversalLoader.loadTexture error loading " + url, arguments));
    const texture = loader.load(
      url,
      onLoad || undefined,
      undefined,
      finalOnError,
    ) as ITextureWithSource;
    texture.sourceFile = url;

    if (mapping) {
      texture.mapping = mapping;
    }

    return texture;
  }

  loadHdrTexture(
    url: string,
    mapping?: Mapping,
    onLoad?: SuccessCallback,
    onError?: ErrorCallback,
    theCrossOrigin: string = this.crossOrigin,
  ): ITextureWithSource {
    const wrappedOnLoad = (texture: DataTexture): void => {
      let pmremGenerator = new PMREMGenerator(STAGE.renderer);
      pmremGenerator.compileEquirectangularShader();
      let envMap = pmremGenerator.fromEquirectangular(texture).texture;
      // das macht die Environment-Tabelle kaputt!!
      // STAGE.scene.environment = envMap;
      // korrekt ist es, das ursprüngliche onLoad aufzurufen:
      if (onLoad) {
        onLoad(texture as ITextureWithSource);
      }
      texture.dispose();
      pmremGenerator.dispose();
    };
    const loader = new RGBELoader(this.loadingManager);
    loader.setCrossOrigin(theCrossOrigin);
    // loader.setDataType(THREE.FloatType); // alt: UnsignedByteType oder FloatType oder HalfFloatType
    const finalOnError: ErrorCallback =
      onError ||
      ((): void => console.warn("UniversalLoader.loadTexture error loading " + url, arguments));
    const texture = loader.load(
      url,
      wrappedOnLoad || undefined,
      undefined,
      finalOnError,
    ) as ITextureWithSource;
    texture.sourceFile = url;

    if (mapping) {
      texture.mapping = mapping;
    }

    return texture;
  }

  /**
   * Lädt einen TextureCube
   */
  loadTextureCube(
    urls: string[],
    mapping?: Mapping,
    onLoad?: SuccessCallback,
    onError?: ErrorCallback,
    theCrossOrigin: string = this.crossOrigin,
  ): ICubeTextureWithSource | null {
    try {
      const loader = new CubeTextureLoader(this.loadingManager);
      loader.setCrossOrigin(theCrossOrigin);

      const finalOnError: ErrorCallback =
        onError ||
        ((): void =>
          console.warn(
            "UniversalLoader.loadTextureCube error loading " + urls.join(", "),
            arguments,
          ));

      const textureCube = loader.load(
        urls,
        onLoad || undefined,
        undefined,
        finalOnError,
      ) as ICubeTextureWithSource;

      textureCube.format = RGBAFormat;
      textureCube.sourceFiles = urls;

      return textureCube;
    } catch (e) {
      console.warn("error loading cube textures ", urls, e);
      return null;
    }
  }

  /**
   * ruft Callback auf, sobald alles geladen wurde
   */
  delayUntilEverythingLoaded(
    callback: () => void,
    nameForDebugging: string = "",
    timeOutCallback?: () => void,
  ): void {
    Commons.delayUntil(
      callback,
      (): boolean => this.isEverythingLoaded(),
      20 * 1000,
      1,
      (): void => {
        console.warn(
          "UniversalLoader.delayUntilEverythingLoaded timeout warning for ",
          nameForDebugging,
          callback,
        );
        this.setEverythingLoaded();
        (timeOutCallback ?? callback)();
      },
    );
  }

  /** */
  startSomeLoading(): void {
    this.loadingManager.isLoading = true;
  }

  /**
   * true, wenn zur Zeit keine Ladevorgänge laufen
   */
  isEverythingLoaded(): boolean {
    return !this.loadingManager.isLoading;
  }

  /** */
  setEverythingLoaded(): void {
    this.loadingManager.reset();
  }

  _callLoad(
    loader: { load: Function },
    url: string,
    successCallback: SuccessCallback,
    progressCallback: ProgressCallback | undefined,
    errorCallback: ErrorCallback,
  ): void {
    // Da UniversalLoader selbst cached, sollte der Loader-Cache (der nur auf FileLoader-Ebene gecached ist und immer noch mal parst) ausgeschaltet werden,
    // ansonsten wird der File-Content beibehalten, welcher recht groß sein kann
    let cachedBefore = Cache.enabled;
    const resetCacheEnable =
      <T>(callback?: (...args: T[]) => unknown) =>
      (...args: T[]): unknown | undefined => {
        Cache.enabled = cachedBefore;
        if (callback) {
          return callback(...args);
        }
      };
    try {
      Cache.enabled = false;
      loader.load(
        url,
        resetCacheEnable(successCallback),
        resetCacheEnable(progressCallback),
        resetCacheEnable(errorCallback),
      );
    } catch (e) {
      Cache.enabled = cachedBefore;
      throw e;
    }
  }
}

export const UniversalLoader = UniversalLoaderClass.INSTANCE;
